import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Users, LogIn, Check, X } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

export default function Join() {
  const { code } = useParams();
  const navigate = useNavigate();
  const { user, loading, refresh } = useAuth();
  const [invite, setInvite] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get(`/household/invites/${code}`).then(r => setInvite(r.data)).catch(e => setError(e?.response?.data?.detail || "Invite not found"));
  }, [code]);

  const signIn = () => { localStorage.setItem("bf_pending_invite", code); navigate("/login"); };
  const accept = async () => {
    setBusy(true);
    try {
      await api.post("/household/join", { code });
      await refresh();
      toast.success(`Welcome to ${invite.household_name}!`);
      navigate("/household", { replace: true });
    } catch (e) { setError(e?.response?.data?.detail || "Could not join"); }
    finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-6" data-testid="join-page">
      <div className="w-full max-w-md rounded-3xl border border-border bg-card p-8 shadow-2xl fade-up">
        <div className="h-12 w-12 rounded-2xl bg-indigo-500/15 text-indigo-400 flex items-center justify-center"><Users className="h-6 w-6" /></div>
        <div className="text-[10px] uppercase tracking-[0.2em] text-indigo-400 font-semibold mt-5">Household invite</div>
        {error ? (
          <>
            <h1 className="font-display text-2xl font-extrabold mt-1 flex items-center gap-2"><X className="h-5 w-5 text-rose-500" /> Can't use this invite</h1>
            <p className="text-sm text-muted-foreground mt-2" data-testid="join-error">{error}</p>
            <button onClick={() => navigate(user ? "/dashboard" : "/login")} className="mt-6 px-4 py-2 rounded-lg border border-border text-sm hover:bg-accent">Go to OWEverit</button>
          </>
        ) : !invite ? <div className="h-24 mt-4 rounded-xl bg-accent animate-pulse" /> : (
          <>
            <h1 className="font-display text-2xl sm:text-3xl font-extrabold mt-1" data-testid="join-title">Join {invite.household_name}</h1>
            <p className="text-sm text-muted-foreground mt-2">
              {invite.invited_by_name} invited you to manage household bills and debts together. You'll both see the same bills, calendar, debts and payoff plan.
              {invite.email && <> This invite is for <span className="font-semibold text-foreground">{invite.email}</span>.</>}
            </p>
            {invite.status !== "pending" ? (
              <div className="mt-6 text-sm text-amber-500 font-semibold" data-testid="join-used">This invite has already been used.</div>
            ) : loading ? null : user ? (
              <>
                <div className="mt-5 p-3 rounded-xl bg-accent/40 border border-border/60 text-xs">
                  Signed in as <span className="font-semibold">{user.name}</span> ({user.email}). Your existing bills and debts will move into the shared household.
                </div>
                <button onClick={accept} disabled={busy} data-testid="join-accept-btn"
                  className="mt-5 w-full flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-indigo-500 text-white font-semibold hover:bg-indigo-600 transition disabled:opacity-60">
                  <Check className="h-4 w-4" /> {busy ? "Joining…" : "Accept & join household"}
                </button>
              </>
            ) : (
              <button onClick={signIn} data-testid="join-signin-btn"
                className="mt-6 w-full flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-indigo-500 text-white font-semibold hover:bg-indigo-600 transition">
                <LogIn className="h-4 w-4" /> Sign in with Google to accept
              </button>
            )}
          </>
        )}
      </div>
    </div>
  );
}
